import { useEffect } from 'react';
import { type Control, useFormState, useWatch } from 'react-hook-form';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: the four material-rate fields (watched), their validation errors,
 *           and each submit attempt.
 * Writes:   the Provider's `isMaterialRatesBlockVisible` state — MaterialsPanel
 *           renders the show/hide PseudoLink label and the
 *           MaterialRateDetails `isVisible` prop from it.
 * Guards:   none — any non-empty value opens the block; all-empty closes it.
 *           A manual PseudoLink toggle holds until the watched values next
 *           change (the effect only re-runs on those). An active error on
 *           any of them keeps the block open after a value change and
 *           reopens it on every submit, so a required rate is never hidden.
 * Legacy:   the legacy form's material-rate visibility toggle.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("material-rate visibility").
 */
export function useMaterialRateVisibility({
  control,
  setIsMaterialRatesBlockVisible,
}: {
  control: Control<OrderFormV3FieldValues>;
  setIsMaterialRatesBlockVisible: (visible: boolean) => void;
}): void {
  const materialRate = useWatch({ control, name: 'materialRate' });
  const materialRateUoM = useWatch({ control, name: 'materialRateUoM' });
  const materialRateBuyPrice = useWatch({ control, name: 'materialRateBuyPrice' });
  const materialRateSellPrice = useWatch({ control, name: 'materialRateSellPrice' });
  const { errors, submitCount } = useFormState({
    control,
    name: ['materialRateUoM', 'materialRateBuyPrice', 'materialRateSellPrice'],
  });
  const hasMaterialRateError = Boolean(
    errors.materialRateUoM || errors.materialRateBuyPrice || errors.materialRateSellPrice,
  );

  useEffect(() => {
    const hasMaterialRates = Boolean(
      materialRate || materialRateUoM || materialRateBuyPrice || materialRateSellPrice,
    );

    setIsMaterialRatesBlockVisible(hasMaterialRates);
    // Legacy deps verbatim (the four watched fields); the setter is read via
    // closure exactly as legacy reads its useState setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialRate, materialRateUoM, materialRateBuyPrice, materialRateSellPrice]);

  useEffect(() => {
    if (hasMaterialRateError) {
      setIsMaterialRatesBlockVisible(true);
    }
  }, [
    hasMaterialRateError,
    submitCount,
    materialRate,
    materialRateUoM,
    materialRateBuyPrice,
    materialRateSellPrice,
    setIsMaterialRatesBlockVisible,
  ]);
}
